/* eslint-disable no-underscore-dangle */
import {
  useCallback, useEffect, useMemo, useState
} from 'react';
import type { FC } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid
} from '@mui/material';
import useIsMountedRef from 'src/hooks/useIsMountedRef';
import useSession from 'src/hooks/useSession';
import { AutocompleteResponse } from 'src/api/dto/search/AutocompleteResponse';
import axios from 'axios';
import { KumaoTheme } from 'src/theme';
import { serverConfig } from 'src/config';
import { OrganizationSearchResponse } from 'src/api/dto/search/OrganizationSearchResponse';
import useSettings from 'src/hooks/useSettings';
import OrgCard from 'src/components/search/OrgCard';
import SearchResetButton from 'src/components/search/SearchResetButton';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SearchResultBranchFilter, { BrancheFacet } from 'src/components/search/SearchResultBranchFilter';
import SearchResultRadiusFilter, { DEFAULT_RADIUS_KM } from 'src/components/search/SearchResultRadiusFilter';
import distanceKm from 'src/utils/distanceKm';

export interface SimpleProps {
  onSearch?: (organizationsFilter: OrganizationSearchResponse) => void;
}

const OrganizationSearchResults: FC<SimpleProps> = (props) => {
  const { onSearch } = props;
  const isMountedRef = useIsMountedRef();
  const { settings } = useSettings();
  const { session } = useSession();
  const [organizations, setOrganizations] = useState<OrganizationSearchResponse | null>(
    null
  );
  const [selectedBrancheIds, setSelectedBrancheIds] = useState<number[]>([]);
  const [userPosition, setUserPosition] = useState<[number, number] | null>(null);
  const [radiusKm, setRadiusKm] = useState<number | null>(null);

  const getOrganizations = useCallback(async (localSearchTerm: string) => {
    const query = {
      query: {
        multi_match: {
          query: localSearchTerm,
          fields: ['company_name^3', 'last_name^3', 'branchesname^2', 'html_content^2', 'zip_code^4', 'city^4', 'county^3', 'street_name^1', 'services^2', 'events^2'],
          fuzziness: 'AUTO',
          fuzzy_transpositions: true
        }
      },
      size: 100
    };
    try {
      const instance = axios.create();
      delete instance.defaults.headers.common.Authorization;
      instance.defaults.headers['Content-Type'] = 'application/json;charset=utf-8';
      console.log(`orgSearchORG: ${localSearchTerm}`);
      const response = await instance.post<OrganizationSearchResponse>(
        serverConfig.searchUrl, JSON.stringify(query), {
          auth: {
            username: 'readall',
            password: 'readonly'
          } }
      );
      if (isMountedRef.current) {
        console.group('SEARCH');
        console.log(response.data);
        console.groupEnd();
        response.data.hits.hits.map((searchObj, i) => (
          response.data.hits.hits[i]._source.branches = JSON.parse(searchObj._source.branchesjson)
        ));
        setOrganizations(response.data);
        if (onSearch) {
          onSearch(response.data);
        }
      }
    } catch (err) {
      console.error(err);
    }
  }, [isMountedRef]);

  const getOrganizationsByBranche = useCallback(async (localSearchTerm: string) => {
    const query = {
      query: {
        multi_match: {
          query: localSearchTerm,
          fields: ['branchesname^7', 'company_name^2', 'last_name^2', 'description^2', 'zip_code^3', 'city^3', 'county^3', 'street_name^1'],
          fuzziness: 'AUTO',
          fuzzy_transpositions: true
        }
      },
      size: 100
    };
    try {
      const instance = axios.create();
      delete instance.defaults.headers.common.Authorization;
      instance.defaults.headers['Content-Type'] = 'application/json;charset=utf-8';
      console.log(`orgSearchORGBYBRANCH: ${localSearchTerm}`);
      const response = await instance.post<OrganizationSearchResponse>(
        serverConfig.searchUrl, JSON.stringify(query), {
          auth: {
            username: 'readall',
            password: 'readonly'
          } }
      );
      if (isMountedRef.current) {
        console.group('SEARCH branch');
        console.log(response.data);
        console.groupEnd();
        response.data.hits.hits.map((searchObj, i) => (
          response.data.hits.hits[i]._source.branches = JSON.parse(searchObj._source.branchesjson)
        ));
        setOrganizations(response.data);
        if (onSearch) {
          onSearch(response.data);
        }
      }
    } catch (err) {
      console.error(err);
    }
  }, [isMountedRef]);

  /**
   * Convert the Autocomplete response into a string list
   * @param data Response from the autocomplete service call
   * @returns String[]: Unique list for autocomplete  suggestions
   */
  const getListFromAutocompleteResponse = (data: AutocompleteResponse) => {
    const items = [];
    data.suggest.autocomplete.map((entries) => (
      entries.options.map((item) => (
        items.push(item.text)
      ))
    ));
    return [...new Set(items)]; // to remove duplicates
  };

  const loadAutocompleteAndSearch = (searchString: string) => {
    if (!searchString || searchString.length === 0) {
      return;
    }

    (async () => {
      const instance = axios.create();
      delete instance.defaults.headers.common.Authorization;
      instance.defaults.headers['Content-Type'] = 'application/json;charset=utf-8';
      const query = {
        suggest: {
          autocomplete: {
            prefix: searchString,
            completion: {
              field: 'content_txt'
            }
          }
        }
      };
      const response = await instance.post<AutocompleteResponse>(
        serverConfig.autoCompleteSearchUrl, JSON.stringify(query), {
          auth: {
            username: 'readall',
            password: 'readonly'
          } }
      );

      if (response) {
        const mylist = getListFromAutocompleteResponse(response.data);
        getOrganizations(`${searchString} ${mylist.join(' ')}`);
      }
    })();
  };

  useEffect(() => {
    console.log(`orgsearchMODE: ${session.selectedSearchMode}`);
    setSelectedBrancheIds([]);
    setUserPosition(null);
    setRadiusKm(null);
    if (session && session.searchTerm) {
      console.log(`orgsearchTERM: ${session.searchTerm}`);
      loadAutocompleteAndSearch(session.searchTerm);
    } else if (session && session.selectedSearchMode && session.selectedSearchMode !== 'Alle') {
      getOrganizationsByBranche(session.selectedSearchMode);
    } else {
      setOrganizations(null);
    }
  }, [session]);

  const brancheFacets = useMemo<BrancheFacet[]>(() => {
    if (!organizations) {
      return [];
    }
    const counts = new Map<number, BrancheFacet>();
    organizations.hits.hits.forEach((hit) => {
      (hit._source.branches || []).forEach((branche) => {
        const existing = counts.get(branche.id);
        if (existing) {
          existing.count += 1;
        } else {
          counts.set(branche.id, { id: branche.id, name: branche.name, count: 1 });
        }
      });
    });
    return [...counts.values()].sort((a, b) => b.count - a.count);
  }, [organizations]);

  const filteredHits = useMemo(() => {
    if (!organizations) {
      return [];
    }
    let { hits } = organizations.hits;
    if (selectedBrancheIds.length > 0) {
      hits = hits.filter((hit) => (
        (hit._source.branches || []).some((branche) => selectedBrancheIds.includes(branche.id))
      ));
    }
    if (userPosition && radiusKm !== null) {
      hits = hits.filter((hit) => {
        const { latitude, longitude } = hit._source;
        if (latitude == null || longitude == null) {
          return false;
        }
        return distanceKm(userPosition[0], userPosition[1], latitude, longitude) <= radiusKm;
      });
    }
    return hits;
  }, [organizations, selectedBrancheIds, userPosition, radiusKm]);

  const handleActivateRadiusFilter = (latitude: number, longitude: number) => {
    setUserPosition([latitude, longitude]);
    setRadiusKm(DEFAULT_RADIUS_KM);
  };

  const handleDeactivateRadiusFilter = () => {
    setUserPosition(null);
    setRadiusKm(null);
  };

  if (!organizations || (session.searchTerm.length === 0 && session.selectedSearchMode === 'Alle')) {
    return null;
  }

  const noResultsFromFilters = filteredHits.length === 0
    && (selectedBrancheIds.length > 0 || radiusKm !== null)
    && organizations.hits.total.value > 0;
  const showSidebar = organizations.hits.total.value > 0;

  const resultsContent = (
    <>
      {(filteredHits.length === 0 && (
        <Box
          sx={{ pl: 4 }}
        >
          <Grid
            container
            spacing={2}
          >
            <Grid item>
              <SentimentDissatisfiedIcon sx={{ fontSize: 30, color: (theme: KumaoTheme) => theme.palette.secondary.main }} />
            </Grid>
            <Grid item>
              <Typography
                sx={{ fontWeight: 'bold', color: (theme: KumaoTheme) => theme.palette.secondary.main }}
              >
                Es tut uns Leid...
              </Typography>
              {noResultsFromFilters ? (
                <Typography
                  sx={{ color: (theme: KumaoTheme) => theme.palette.secondary.main }}
                >
                  Für die gewählten Filter gibt es leider keine Treffer.
                </Typography>
              ) : (
                <>
                  <Typography
                    sx={{ color: (theme: KumaoTheme) => theme.palette.secondary.main }}
                  >
                    Leider kein Treffer, mit diesem Suchbegriff!
                  </Typography>
                  <Typography
                    sx={{ color: (theme: KumaoTheme) => theme.palette.secondary.main, pt: 2 }}
                  >
                    Du kannst gerne die Kategorien oder Newcomer-Bereich nutzen, um zu schauen welche Dienstleister bereits auf Kumao sind.
                  </Typography>
                </>
              )}
            </Grid>
          </Grid>
        </Box>
      ))}
      {filteredHits.map((org) => (
        <OrgCard
          key={org._id}
          orgCardSearch={org._source}
        />
      ))}
    </>
  );

  return (
    <Container
      maxWidth={settings.compact ? 'xl' : false}
      sx={{
        paddingTop: 1,
        paddingBottom: 5,
        borderRadius: 5,
        marginBottom: 3
      }}
    >
      <Grid
        justifyContent="space-between"
        container
        spacing={2}
      >
        <Grid
          item
        >
          <Typography
            variant="h3"
            sx={{
              pl: 3,
              color: (theme: KumaoTheme) => theme.palette.primary[600],
              pt: 0.5
            }}
          >
            Suchergebnisse
          </Typography>
        </Grid>
        <Grid
          item
        >
          <SearchResetButton />
        </Grid>
      </Grid>

      {showSidebar ? (
        <Grid
          container
          spacing={2}
          sx={{ pt: 2 }}
        >
          <Grid
            item
            xs={12}
            md={3}
          >
            <SearchResultBranchFilter
              branches={brancheFacets}
              selectedBrancheIds={selectedBrancheIds}
              onChange={setSelectedBrancheIds}
            />
            <SearchResultRadiusFilter
              radiusKm={radiusKm}
              onActivate={handleActivateRadiusFilter}
              onDeactivate={handleDeactivateRadiusFilter}
              onRadiusChange={setRadiusKm}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={9}
          >
            <Box
              component="div"
              sx={{ display: 'flex', flexWrap: 'wrap' }}
            >
              {resultsContent}
            </Box>
          </Grid>
        </Grid>
      ) : (
        <Box
          component="div"
          sx={{ display: 'flex',
            flexWrap: 'wrap',
            pt: 2, }}
        >
          {resultsContent}
        </Box>
      )}
    </Container>
  );
};

export default OrganizationSearchResults;
